import React from "react";
import faqImage from "@/images/site/faq.jpg";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import Container from "../site/Container";
import CtaBand from "../site/CtaBand";
import CtaLink from "../site/CtaLink";
import PageHeader from "../site/PageHeader";

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: "What is BuySooner Boost?",
    answer: (
      <p>
        BuySooner Boost gives you more buying power today. We contribute an additional amount on top of your deposit
        and home loan, so you can purchase the home you want now and repay the Boost later, typically with a refinance.
      </p>
    ),
  },
  {
    question: "How is it different to a second mortgage?",
    answer: (
      <p>
        A second mortgage is another loan on top of your home loan, usually at a much higher interest rate, and it still
        leaves a significant affordability gap. With BuySooner Boost there is no interest or fees to pay us until you
        refinance or sell your home, so you are not likely to run into affordability issues.
      </p>
    ),
  },
  {
    question: "Who is eligible?",
    answer: (
      <p>
        Eligibility depends on your situation and the home you want to buy. Complete our short form and we’ll let you
        know if you’re eligible for a BuySooner Boost.
      </p>
    ),
  },
  {
    question: "How much can I get?",
    answer: (
      <p>
        The size of your Boost depends on your situation and the value of the home you want to buy. Complete our short
        form to find out if you’re eligible.
      </p>
    ),
  },
  {
    question: "What does it cost?",
    answer: (
      <>
        <p>There is no interest and there are no fees to pay us until you refinance or sell your home.</p>
        <p>
          In exchange for the Boost, we receive a small share of your home’s value when you refinance or sell. How much
          depends on the size of your Boost and the value of the property, but it is usually a number like 6%, not a
          number like 30% that some other shared equity schemes take.
        </p>
      </>
    ),
  },
  {
    question: "When do I repay the Boost?",
    answer: (
      <p>
        When your home’s value and your situation allow, you refinance to a standard mortgage and repay the Boost,
        typically within 3–5 years. You can also pay a small amount monthly, or buy out our share in stages.
      </p>
    ),
  },
  {
    question: "What happens if property prices don’t increase?",
    answer: (
      <p>
        How much your home will go up cannot be guaranteed. If it does not appreciate well, it will take longer to be in
        profit after the cost of the Boost. If you need more time to repay, we will generally give you further
        extensions, so even a further 2 or 3 years is fine.
      </p>
    ),
  },
  {
    question: "Is there any impact on my ownership?",
    answer: (
      <p>
        The home is all in your name and the title says it is 100% yours. The Boost is a private contract: when you
        refinance or sell, we receive our share, and then the home is all yours.
      </p>
    ),
  },
  {
    question: "How does the application process work?",
    answer: (
      <p>
        Start with our short form, which takes about 3 minutes. We’ll assess your eligibility and let you know if you’re
        eligible for a BuySooner Boost. If you are, we provide the Boost on top of your deposit and loan, you buy your
        home, and you repay the Boost when you refinance.
      </p>
    ),
  },
  {
    question: "Is there any obligation to proceed?",
    answer: <p>No. Checking your eligibility doesn’t commit you to anything.</p>,
  },
];

const FAQ = () => {
  return (
    <>
      <section className="py-14 sm:py-20">
        <Container>
          <PageHeader eyebrow="FAQs" title="Frequently asked questions." />

          <div className="mt-10 grid gap-10 lg:grid-cols-[1.7fr_1fr] lg:gap-14">
            <Accordion type="single" collapsible>
              {faqs.map((faq) => (
                <AccordionItem key={faq.question} value={faq.question} className="border-b border-slate-200">
                  <AccordionTrigger className="items-center py-4 text-base text-navy hover:no-underline **:data-[slot=accordion-trigger-icon]:text-navy">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pr-8 text-[15px] leading-relaxed text-slate-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <aside className="space-y-4">
              <div className="rounded-2xl bg-mint p-7">
                <h2 className="text-lg font-semibold text-navy">Still have questions?</h2>
                <p className="mt-1 text-slate-600">We’re here to help.</p>
                <CtaLink to="/contact-us" size="sm" className="mt-5">
                  Get in touch
                </CtaLink>
              </div>
              <img src={faqImage} alt="" className="aspect-[4/3] w-full rounded-2xl object-cover lg:aspect-[5/6]" />
            </aside>
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
};

export default FAQ;
